export interface ShowTokenWhyMoveTldrParseResult {
  summary?: string;
  options?: string[];
}

/**
 * 从字符串中解析 show_token_why_move_tldr(...) 或 show_stock_why_move_tldr(...) 的参数，返回 summary 与 options。
 * - 支持换行、空格
 * - 支持单双引号字符串
 * - 支持数组内多项、尾随逗号
 * - 忽略字符串内部的括号/逗号
 * - 支持 # 注释（忽略注释行和行内注释）
 */
export const fnNames = ["show_token_why_move_tldr", "show_stock_why_move_tldr"];
export const parseShowTokenWhyMoveTldr = (
  input: string = "",
): ShowTokenWhyMoveTldrParseResult | null => {
  if (!input) {
    return null;
  }

  let fnIndex = -1;
  let fnName = "";

  // 查找第一个匹配的函数名
  for (const name of fnNames) {
    const index = input.indexOf(name);
    if (index !== -1) {
      fnIndex = index;
      fnName = name;
      break;
    }
  }

  if (fnIndex === -1) {
    return null;
  }

  const startParenIndex = input.indexOf("(", fnIndex + fnName.length);
  if (startParenIndex === -1) {
    return null;
  }

  // 提取与之匹配的右括号，忽略字符串中的括号
  const endParenIndex = findMatchingParenIndex(input, startParenIndex);
  if (endParenIndex === -1) {
    return null;
  }

  const argsContent = input.slice(startParenIndex + 1, endParenIndex);

  // 预处理：移除注释
  const cleanedArgsContent = removeComments(argsContent);

  const namedArgs = splitTopLevel(cleanedArgsContent, ",")
    .map((seg) => seg.trim())
    .filter(Boolean);

  let summary: string | undefined;
  let options: string[] | undefined;

  for (const pair of namedArgs) {
    const eqIndex = findTopLevelChar(pair, "=");
    if (eqIndex === -1) {
      continue;
    }

    const rawKey = pair.slice(0, eqIndex).trim();
    const rawVal = pair.slice(eqIndex + 1).trim();

    if (rawKey === "summary") {
      const parsed = parseStringLiteral(rawVal);
      if (parsed.ok) {
        summary = parsed.value;
      }
    } else if (rawKey === "options") {
      const parsed = parseArrayOfStrings(rawVal);
      if (parsed.ok) {
        options = parsed.value;
      }
    }
  }

  if (summary === undefined && options === undefined) {
    return null;
  }
  return { summary, options };
};

/**
 * 移除注释的函数
 * - 移除以 # 开头的整行注释
 * - 移除行内注释（但保留字符串内的 # 号）
 */
function removeComments(source: string): string {
  const lines = source.split("\n");
  const cleanedLines: string[] = [];

  for (const line of lines) {
    const cleanedLine = removeInlineComment(line);
    // 如果清理后的行不为空（不是纯注释行），则保留
    if (cleanedLine.trim() !== "") {
      cleanedLines.push(cleanedLine);
    }
  }

  return cleanedLines.join("\n");
}

/**
 * 移除行内注释，但保留字符串内的 # 号
 */
function removeInlineComment(line: string): string {
  let inSingle = false;
  let inDouble = false;
  let result = "";

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];

    // 处理转义字符
    if (ch === "\\" && (inSingle || inDouble)) {
      result += ch;
      if (i + 1 < line.length) {
        result += line[i + 1];
        i += 1;
      }
      continue;
    }

    // 处理单引号
    if (!inDouble && ch === "'" && !inSingle) {
      inSingle = true;
      result += ch;
      continue;
    }
    if (inSingle && ch === "'") {
      inSingle = false;
      result += ch;
      continue;
    }

    // 处理双引号
    if (!inSingle && ch === '"' && !inDouble) {
      inDouble = true;
      result += ch;
      continue;
    }
    if (inDouble && ch === '"') {
      inDouble = false;
      result += ch;
      continue;
    }

    // 如果在字符串内，直接添加字符
    if (inSingle || inDouble) {
      result += ch;
      continue;
    }

    // 如果遇到注释符号且不在字符串内，截断该行
    if (ch === "#") {
      break;
    }

    result += ch;
  }

  return result;
}

// 工具方法
function findMatchingParenIndex(source: string, openIndex: number): number {
  let depth = 0;
  let inSingle = false;
  let inDouble = false;
  for (let i = openIndex; i < source.length; i++) {
    const ch = source[i];

    if (ch === "\\" && (inSingle || inDouble)) {
      i += 1; // 跳过转义字符
      continue;
    }

    if (!inDouble && ch === "'" && !inSingle) {
      inSingle = true;
      continue;
    }
    if (inSingle && ch === "'") {
      inSingle = false;
      continue;
    }

    if (!inSingle && ch === '"' && !inDouble) {
      inDouble = true;
      continue;
    }
    if (inDouble && ch === '"') {
      inDouble = false;
      continue;
    }

    if (inSingle || inDouble) {
      continue;
    }

    if (ch === "(") {
      depth += 1;
    }
    if (ch === ")") {
      depth -= 1;
      if (depth === 0) {
        return i;
      }
    }
  }
  return -1;
}

function splitTopLevel(source: string, delimiter: "," | ";"): string[] {
  const result: string[] = [];
  let inSingle = false;
  let inDouble = false;
  let inBracket = 0; // [ ]
  let inParen = 0; // ( )
  let current = "";

  for (let i = 0; i < source.length; i++) {
    const ch = source[i];

    if (ch === "\\" && (inSingle || inDouble)) {
      current += ch;
      if (i + 1 < source.length) {
        current += source[i + 1];
      }
      i += 1;
      continue;
    }

    if (!inDouble && ch === "'" && !inSingle) {
      inSingle = true;
      current += ch;
      continue;
    }
    if (inSingle && ch === "'") {
      inSingle = false;
      current += ch;
      continue;
    }

    if (!inSingle && ch === '"' && !inDouble) {
      inDouble = true;
      current += ch;
      continue;
    }
    if (inDouble && ch === '"') {
      inDouble = false;
      current += ch;
      continue;
    }

    if (inSingle || inDouble) {
      current += ch;
      continue;
    }

    if (ch === "[") {
      inBracket += 1;
    } else if (ch === "]") {
      inBracket -= 1;
    } else if (ch === "(") {
      inParen += 1;
    } else if (ch === ")") {
      inParen -= 1;
    }

    if (ch === delimiter && inBracket === 0 && inParen === 0) {
      result.push(current);
      current = "";
    } else {
      current += ch;
    }
  }
  if (current) {
    result.push(current);
  }
  return result;
}

function findTopLevelChar(source: string, char: string): number {
  let inSingle = false;
  let inDouble = false;
  let inBracket = 0;
  let inParen = 0;
  for (let i = 0; i < source.length; i++) {
    const ch = source[i];

    if (ch === "\\" && (inSingle || inDouble)) {
      i += 1;
      continue;
    }

    if (!inDouble && ch === "'" && !inSingle) {
      inSingle = true;
      continue;
    }
    if (inSingle && ch === "'") {
      inSingle = false;
      continue;
    }

    if (!inSingle && ch === '"' && !inDouble) {
      inDouble = true;
      continue;
    }
    if (inDouble && ch === '"') {
      inDouble = false;
      continue;
    }

    if (inSingle || inDouble) {
      continue;
    }

    if (ch === "[") {
      inBracket += 1;
    } else if (ch === "]") {
      inBracket -= 1;
    } else if (ch === "(") {
      inParen += 1;
    } else if (ch === ")") {
      inParen -= 1;
    }

    if (ch === char && inBracket === 0 && inParen === 0) {
      return i;
    }
  }
  return -1;
}

function parseStringLiteral(raw: string): { ok: boolean; value?: string } {
  const trimmed = raw.trim();
  if (!trimmed) {
    return { ok: false };
  }

  const quote = trimmed[0];
  if (
    (quote !== '"' && quote !== "'") ||
    trimmed[trimmed.length - 1] !== quote
  ) {
    return { ok: false };
  }

  let out = "";
  for (let i = 1; i < trimmed.length - 1; i++) {
    const ch = trimmed[i];
    if (ch === "\\") {
      const next = trimmed[i + 1];
      if (next === '"' || next === "'" || next === "\\") {
        out += next;
        i += 1;
      } else if (next === "n") {
        out += "\n";
        i += 1;
      } else if (next === "t") {
        out += "\t";
        i += 1;
      } else {
        // 其他转义，原样输出
        out += next;
        i += 1;
      }
    } else {
      out += ch;
    }
  }
  return { ok: true, value: out };
}

function parseArrayOfStrings(raw: string): { ok: boolean; value?: string[] } {
  const trimmed = raw.trim();
  if (!trimmed.startsWith("[") || !trimmed.endsWith("]")) {
    return { ok: false };
  }
  const inner = trimmed.slice(1, -1);
  const items = splitTopLevel(inner, ",")
    .map((seg) => seg.trim())
    .filter(Boolean)
    .map((seg) => {
      // 允许带尾随逗号的最后空项
      const parsed = parseStringLiteral(seg);
      if (parsed.ok) {
        return parsed.value as string;
      }
      return undefined;
    })
    .filter((v): v is string => typeof v === "string");
  return { ok: true, value: items };
}
